/**
 * ChatList component for displaying conversation messages
 * Handles rendering of user and AI messages, loading states, references,
 * and interactive actions like copying, sharing, and text-to-speech
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import { motion } from 'framer-motion';
import { Theme } from '../../theme/lightTheme';
import { ChatListProps, Message, CategoryItem } from '../../interfaces/components/chatList';
import { tr } from '../../translation';
import { IoCopyOutline, IoCheckmark, IoBugOutline, IoStopOutline, IoPlayOutline } from 'react-icons/io5';
import { toast } from 'sonner';
import { openUrl } from '../../utils/helper';
import { BiUser } from 'react-icons/bi';
import CustomTypeWriter from '../customTypeWriter';
import CodeDisplayBlock from '../codeDisplayBlock';
import { renderArticleContent } from '../../utils/html';
import MessageSkeletons from '../messageSkeletons';
import ReferencesCarousel from '../referencesCarousel';
import { RiShare2Line } from 'react-icons/ri';
import { TbFileExport } from 'react-icons/tb';
import { exportChatToPdf } from '../../utils/pdf';
import { GoStar, GoStarFill } from 'react-icons/go';
import { ReferenceItemType } from 'interfaces/components/referencesCarousel';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { HiArrowPath } from 'react-icons/hi2';

/**
 * Main container for the chat list
 * Provides scrolling and styling for the entire chat interface
 */
const Container = styled.div<{ isRTL?: boolean; fullHeight?: boolean }>`
  width: ${({ theme }) => (theme.isMobile ? 100 : 100)}%;
  align-self: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  user-select: text !important;
  margin-bottom: 1rem;
  height: ${({ fullHeight }) => (fullHeight ? '100vh' : '90vh')};
  margin-bottom: 0;
  overflow-y: scroll;

  /* Mobile-specific styles */
  @media (max-width: 768px) {
    -webkit-overflow-scrolling: touch; /* For smooth scrolling on iOS */
    overscroll-behavior: contain; /* Prevent unwanted scrolling behavior on mobile */
    padding-bottom: ${({ fullHeight }) => (fullHeight ? '0' : '60px')};
    height: ${({ fullHeight }) => (fullHeight ? '100vh' : '100vh')};
  }

  a {
    color: ${({ theme }) => theme.text.light};
    font-weight: bold;
    word-break: break-all;
    outline: none;
  }

  /* Style for Markdown-rendered tables */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
    font-size: ${({ theme }) => theme.text.s8};
    text-align: left;
    border: 1px solid ${({ theme }) => theme.border}; /* Outer border */
  }

  thead {
    background-color: ${({ theme }) => theme.table.thead};
  }

  th {
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    border: 1px solid ${({ theme }) => theme.border}; /* Grid lines for header */
    color: ${({ theme }) => theme.table.th};
  }

  tr {
    transition: background-color 0.2s ease;
  }

  /* Alternate row background for even rows */
  tr:nth-child(even) {
    background-color: ${({ theme }) => theme.table.evenRow};
  }

  /* Hover effect for rows */
  tr:hover {
    background-color: ${({ theme }) => theme.table.trHover};
  }

  td {
    padding: 12px 16px;
    border: 1px solid ${({ theme }) => theme.border}; /* Grid lines for cells */
    color: ${({ theme }) => theme.table.text};
  }
`;

const MessagesContainer = styled.div<{ isRTL?: boolean; isEmpty?: boolean; fullHeight?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
  text-wrap: wrap;
  justify-content: flex-end;
  padding-bottom: ${({ theme }) => theme.text.s1};
  height: ${({ fullHeight }) => (fullHeight ? '100vh' : '78vh')};
  pointer-events: ${({ isEmpty }) => (isEmpty ? 'none' : 'auto')};
  padding-top: ${({ fullHeight }) => (fullHeight ? '100px' : '20px')};

  /* When empty, don't take up space in the layout */
  ${({ isEmpty }) =>
    isEmpty &&
    `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: -1;
  `}

  /* Mobile-specific styles */
  @media (max-width: 768px) {
    height: ${({ fullHeight }) => (fullHeight ? '100vh' : '72vh')};
    padding-bottom: ${({ fullHeight }) => (fullHeight ? '0' : '80px')};
  }
`;

const MessageItem = styled(motion.div)<{ isUser: boolean; isRTL?: boolean }>`
  display: flex;
  position: relative;
  flex-direction: column;
  overflow: visible;
  gap: 1rem;
  padding: 1rem;
  align-items: ${({ isUser, isRTL }) => {
    if (isRTL) {
      return isUser ? 'flex-start' : 'flex-end';
    }
    return isUser ? 'flex-end' : 'flex-start';
  }};
`;

const MessageBubble = styled.span<{ isUser?: boolean; isRTL?: boolean }>`
  padding: 1rem;
  max-width: ${({ theme }) => (theme.isMobile ? '73vw' : '80%')};
  overflow-x: auto;
  min-width: 8vw;
  background: ${({ theme }) => theme.voiceQuestionInput.bubbleBackground};
  flex-direction: column;
  border-radius: ${({ isUser, isRTL }) => {
    if (isRTL) {
      return isUser ? '20px 20px 20px 3px' : '20px 20px 3px 20px';
    }
    return isUser ? '20px 20px 3px 20px' : '20px 20px 20px 3px';
  }};
  font-size: ${({ theme }) => theme.text.s5};
  text-align: ${({ isRTL }) => (isRTL ? 'right' : 'left')};
  direction: ${({ isRTL }) => (isRTL ? 'rtl' : 'ltr')};

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.scrollbar.track};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.scrollbar.thumb};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.scrollbar.thumbHover};
  }

  scrollbar-width: thin;
  color: ${({ theme }) => theme.voiceQuestionInput.text} !important;
  scrollbar-color: ${({ theme }) => `${theme.scrollbar.thumb} ${theme.scrollbar.track}`};

  @media (max-width: 768px) {
    max-width: 85vw;
    font-size: ${({ theme }) => theme.text.s6};
    padding: 0.75rem;
    word-break: break-word;
  }
`;

const AvatarContainer = styled(motion.div)`
  width: ${({ theme }) => theme.text.s3};
  aspect-ratio: 1;
  border-radius: ${({ theme }) => `calc(${theme.text.s2} / 2)`};
  overflow: hidden;
  align-items: center;
  justify-content: center;
  margin-bottom: -0.5%;
  border: 1px solid ${({ theme }) => theme.primary};
  display: flex;
  background: ${({ theme }) => theme.voiceQuestionInput.bubbleBackground};
  box-shadow: 0 4px 8px ${({ theme }) => theme.shadow};
  border: ${({ theme }) => theme.text.grey + '22'} solid 1px;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
`;

const LoadingBubbleContainer = styled(motion.div)`
  display: flex;
  padding-left: 1rem;
  padding-bottom: 1rem;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
`;

const OptionIconContainer = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  border-radius: 8px;
  gap: 6px;
  user-select: none;
  color: ${({ theme }) => theme.text.optionIcon};
  font-size: 0.875rem;
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: 0.2s ease;
  min-width: 80px;
  font-weight: 500;

  &:hover {
    background-color: ${({ theme }) => theme.text.light}10 !important;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    min-width: auto;
    padding: 6px 4px;
    font-size: 0.75rem;
    flex: 0 0 auto;
  }
`;
const AssistantOptionContainer = styled(motion.div)<{ isUser: boolean; isRTL?: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.text.s11};
  align-items: center;
  flex-direction: row;
  bottom: 5px;
  z-index: 2;
  justify-content: ${({ isUser, isRTL }) => {
    if (isRTL) {
      return isUser ? 'flex-end' : 'flex-start';
    }
    return isUser ? 'flex-end' : 'flex-start';
  }};

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0.25rem;
    justify-content: center;
    margin-top: 0.5rem;
  }
`;

const EmptyMessageContainer = styled.div<{ isRTL?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.text.s3};
  color: ${({ theme }) => theme.text.light};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  width: 70%;

  /* Mobile-specific styles */
  @media (max-width: 768px) {
    position: static;
    transform: none;
    padding: 4rem 1rem 8rem;
    width: 100%;
    height: auto;
    min-height: 70vh;
    justify-content: flex-start;
  }
`;

const AssistantMessageContainer = styled(motion.div)<{ isRTL?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0 0;
  width: ${({ theme }) => (theme.isMobile ? '74vw' : 'calc(100vw - 30rem)')};
  color: ${({ theme }) => theme.text.dark};
  text-align: ${({ isRTL }) => (isRTL ? 'right' : 'left')};
  
  .message {
    direction: ${({ isRTL }) => (isRTL ? 'rtl' : 'ltr')};
    line-height: 1.8;
    
    a {
      font-weight: bold;
      word-break: break-all;
      padding: 0 5px;
      border-radius: 4px;
      text-decoration: auto;
      transition: all 0.4s ease;
    }
  }

  @media (max-width: 768px) {
    width: 80vw;
    padding: 1rem 0.5rem 0;
    overflow-x: hidden;
  }
`;

const QuickQuestionsContainer = styled.div<{ isRTL?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  animation: fadeIn 0.5s ease-in-out forwards;
  width: 100%;
  
  @media (min-width: 769px) {
    max-height: 50vh;
    overflow-y: auto;
    /* Custom scrollbar for desktop */
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: ${({ theme }) => theme.scrollbar.track};
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.scrollbar.thumb};
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${({ theme }) => theme.scrollbar.thumbHover};
    }
    
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => `${theme.scrollbar.thumb} ${theme.scrollbar.track}`};
  }

  /* Mobile-specific styles */
  @media (max-width: 768px) {
    padding: 0 0.5rem;
    margin-top: 0.5rem;
    margin-bottom: 2rem;
    gap: 0.5rem;
  }
`;

const QuickQuestion = styled(motion.button)`
  background: ${({ theme }) => theme.voiceQuestionInput.bubbleBackground};
  color: ${({ theme }) => theme.text.light};
  border: none;
  padding: 0.8rem 1.2rem;
  border-radius: 20px;
  font-size: ${({ theme }) => theme.text.s9};
  cursor: pointer;
  transition:
    background 0.2s ease-in-out,
    transform 0.2s ease-in-out;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${({ theme }) => theme.voiceQuestionInput.mainButtonStartColor + 'ee'};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  /* Mobile-specific styles */
  @media (max-width: 768px) {
    text-align: center;
    word-break: break-word;
    hyphens: auto;
    width: calc(50% - 0.25rem);
    margin: 0.125rem;
    flex: 0 0 auto;
    font-size: ${({ theme }) => theme.text.s10};
    padding: 0.6rem 0.8rem;
    justify-content: center;
  }
`;

const textVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const AnimatedIntroText = styled(motion.p)`
  font-size: ${({ theme }) => theme.text.s3};
  color: ${({ theme }) => theme.text.light};
  margin-bottom: 1rem;
  text-align: center;
  opacity: 0;

  @media (max-width: 768px) {
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
`;

const LoadingAnimation = styled.div`
  font-size: ${({ theme }) => theme.text.s7};
  margin: 0;
  background: linear-gradient(-90deg, #7a7a7a 0%, ${({ theme }) => theme.text.shine} 50%, #7a7a7a 100%);
  background-size: 200% auto;
  animation: shine 2s linear infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @keyframes shine {
    to {
      background-position: -200% center;
    }
  }
`;

const CategoryBullet = styled(motion.button)<{ isSelected?: boolean }>`
  background: ${({ theme, isSelected }) =>
    isSelected ? theme.categoryBullet.selectedBackground : theme.categoryBullet.background};
  color: ${({ theme, isSelected }) => (isSelected ? theme.categoryBullet.selectedText : theme.categoryBullet.text)};
  border: ${({ theme, isSelected }) => (isSelected ? `1px solid ${theme.categoryBullet.selectedBorder}` : 'none')};
  padding: 0.8rem 1.2rem;
  border-radius: 20px;
  font-size: ${({ theme }) => theme.text.s9};
  cursor: pointer;
  transition:
    background 0.3s ease-in-out,
    color 0.3s ease-in-out,
    border 0.3s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ isSelected }) => (isSelected ? '600' : '400')};
  min-height: 40px;
  min-width: 100px;

  &:hover {
    background: ${({ theme, isSelected }) =>
      isSelected ? theme.categoryBullet.selectedHoverBackground : theme.categoryBullet.hoverBackground};
  }

  /* Mobile-specific styles */
  @media (max-width: 768px) {
    text-align: center;
    word-break: break-word;
    hyphens: auto;
    width: calc(50% - 0.25rem);
    margin: 0.125rem;
    flex: 0 0 auto;
    font-size: ${({ theme }) => theme.text.s10};
    padding: 0.6rem 0.8rem;
    min-height: 34px;
    min-width: 0;
  }
`;

const CategoryLink = styled(motion.button)<{ isRTL?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0.5rem 0 0.35rem 0;
  margin-top: 0.5rem;
  margin-bottom: 0;
  border-radius: 0;
  background: transparent;
  color: ${({ theme }) => theme.voiceQuestionInput.text};
  font-size: ${({ theme }) => theme.text.s9};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.voiceQuestionInput.text}33;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  width: 100%;
  gap: 0.35rem;
  font-weight: 500;
  text-decoration: none;
  
  svg {
    color: ${({ theme }) => theme.voiceQuestionInput.text};
    transition: transform 0.3s ease;
    margin-right: 0.2rem;
  }

  &:hover {
    opacity: 0.8;
    
    svg {
      transform: rotate(180deg);
    }
  }

  /* Mobile-specific styles */
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.text.s10};
    padding: 0.4rem 0 0.3rem 0;
  }
`;

/**
 * ChatList component displays a conversation between user and AI
 * Handles message rendering, loading states, and interactive actions
 *
 * @param messages - Array of message objects to display
 * @param isLoading - Whether the component is in a loading state
 * @param isLoadingMessage - Whether a message is currently being generated
 * @param imageUri - Optional user avatar image URI
 * @param onReadTextPress - Handler for text-to-speech functionality
 * @param isSpeaking - Whether text-to-speech is currently active
 * @param categories - Array of available message categories
 * @param onCategoryPress - Handler for category selection
 * @param selectedCategory - Currently selected category
 * @param isLoadingMessages - Whether messages are being loaded
 * @param onDebugButtonPress - Handler for debug button clicks
 * @param onQuickQuestionPress - Handler for quick question selection
 * @param onSharePress - Handler for sharing conversation
 * @param onAddToFavorite - Handler for adding conversation to favorites
 * @param shared - Whether the conversation is in shared mode
 * @param isFavorite - Whether the conversation is marked as favorite
 * @param onTryWithOtherCategory - Handler for trying a question with a different category
 * @param fullHeight - Whether the chat list should take up the full height of the screen
 * @returns Rendered chat interface with messages and interactive elements
 */
const ChatList = ({
  messages,
  isLoading,
  isLoadingMessage,
  imageUri,
  onReadTextPress,
  isSpeaking,
  categories,
  onCategoryPress,
  selectedCategory,
  isLoadingMessages,
  onQuickQuestionPress,
  onSharePress,
  onAddToFavorite,
  shared = false,
  isFavorite = false,
  onTryWithOtherCategory,
  fullHeight = false,
}: ChatListProps) => {
  const theme = useTheme() as Theme;
  const [isCopied, setIsCopied] = React.useState(false);
  const [speakingMessageIdx, setSpeakingMessageIdx] = useState<number>(-1);
  const [isImageError, setIsImageError] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [dynamicWords, setDynamicWords] = useState('Thinking...');
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const hasScrolled = useRef(false);

  // Get the layout direction from Redux store instead of selected category
  const { selectedChatLayoutDirection } = useSelector((state: RootState) => state.chat);
  const isRTL = selectedChatLayoutDirection === 'rtl';

  /**
   * List of predefined quick questions for user interaction
   */
  const quickQuestions = [
    'How to recognize stroke symptoms?',
    'What are the early warning signs of a heart attack?',
    'What are the signs of high blood pressure?',
    'What are the risk factors for diabetes?',
    'How to respond to a medical emergency?',
    'What are common treatments for anxiety?',
  ];

  /**
   * Converts an array of reference objects to an array of URL strings
   * @param refs - Array of reference objects
   * @returns Array of valid URL strings
   */
  const convertReferencesToLinks = (refs?: ReferenceItemType[]): string[] => {
    if (!refs || refs.length === 0) return [];

    return refs.flatMap((ref) => {
      const links: string[] = [];
      if (ref.full_link && ref.full_link.trim() !== '') links.push(ref.full_link);
      if (ref.genio_url && ref.genio_url.trim() !== '') links.push(ref.genio_url);
      return links;
    });
  };

  /**
   * Checks if the scroll container is near the bottom
   * Used to determine if auto-scrolling should be enabled
   * @returns Boolean indicating if the view is near the bottom
   */
  const isNearBottom = () => {
    const container = containerRef.current;
    if (!container) return true;
    const threshold = 150; // Increased threshold to detect if we're near bottom
    return container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
  };

  /**
   * Handles scroll events on the chat container
   * Updates auto-scroll behavior based on user interaction
   */
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    // Check if user has scrolled to bottom (or very close to it)
    const nearBottom = isNearBottom();

    // Only update if the value changes to prevent unnecessary re-renders
    if (nearBottom !== shouldAutoScroll) {
      setShouldAutoScroll(nearBottom);
    }
  }, [isNearBottom, shouldAutoScroll]);

  // Set up scroll listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  /**
   * Copies message content and references to clipboard
   * Shows a success toast notification
   *
   * @param text - Message text to copy
   * @param references - Array of references to include
   */
  const copyToClipboard = (text: string, references: ReferenceItemType[]) => {
    const articleReferences = new Map<string, string>(); // Map of normalized URL to original URL
  
    // Custom Markdown-style link parser that handles parentheses in URLs
    const extractMarkdownLinksWithUrls = (input: string): string[] => {
      const links: string[] = [];
      const pattern = /\[([^\]]+)\]\(/g; // Match start of [text](...
      let match;
  
      while ((match = pattern.exec(input)) !== null) {
        let startIndex = match.index + match[0].length;
        let url = '';
        let openParens = 1;
  
        for (let i = startIndex; i < input.length; i++) {
          const char = input[i];
          if (char === '(') openParens++;
          else if (char === ')') openParens--;
  
          url += char;
  
          if (openParens === 0) {
            // Remove the last `)` from the URL
            url = url.slice(0, -1);
            if (url.startsWith('http')) {
              links.push(url);
            }
            break;
          }
        }
      }
  
      return links;
    };
  
    // Find all references in the article text
    const extractedUrls = extractMarkdownLinksWithUrls(text);
    extractedUrls.forEach(url => {
      articleReferences.set(url, url);
    });
  
    // If we have less than 5 references in the article, supplement with additional ones
    const remainingSlots = Math.max(0, 5 - articleReferences.size);
    let totalReferences: { url: string; originalUrl: string }[] = [];
  
    // First add all references found in the article
    articleReferences.forEach((originalUrl) => {
      totalReferences.push({ url: originalUrl, originalUrl });
    });
  
    // If we need more references, add them from the references list
    if (remainingSlots > 0 && Array.isArray(references)) {
      const availableRefs = references.filter(ref => {
        const refUrl = ref.full_link || ref.genio_url || '';
        return !articleReferences.has(refUrl);
      });
  
      // Add remaining references up to the limit
      availableRefs.slice(0, remainingSlots).forEach(ref => {
        const refUrl = ref.full_link || ref.genio_url || '';
        totalReferences.push({ url: refUrl, originalUrl: refUrl });
      });
    }
  
    // Create a map of URLs to their reference numbers
    const urlToNumber = new Map<string, number>();
    totalReferences.forEach((ref, index) => {
      urlToNumber.set(ref.url, index + 1);
    });
  
    let processedText = text;
    const pattern = /\[([^\]]+)\]\(/g;
    let match;
    
    while ((match = pattern.exec(text)) !== null) {
      const label = match[1];
      let startIndex = match.index + match[0].length;
      let url = '';
      let openParens = 1;
    
      for (let i = startIndex; i < text.length; i++) {
        const char = text[i];
        if (char === '(') openParens++;
        else if (char === ')') openParens--;
    
        url += char;
    
        if (openParens === 0) {
          url = url.slice(0, -1); // Remove final ')'
    
          // Safely match using raw or decoded
          let number = urlToNumber.get(url);
          if (!number) {
            try {
              const decoded = decodeURIComponent(url);
              number = urlToNumber.get(decoded);
            } catch (err) {
              // URI was malformed — ignore decoding
            }
          }
    
          if (number) {
            const originalLink = `[${label}](${url})`;
            const numberedLink = `[${number}](${url})`;
            processedText = processedText.replace(originalLink, numberedLink);
          }
    
          break;
        }
      }
    }
  
    // Format references text
    const referencesText = totalReferences.length > 0
      ? totalReferences.reduce((acc, ref, index) => {
          return acc + `[${index + 1}] ${ref.originalUrl}\n`;
        }, '###References:\n')
      : '';
  
    const finalText = `${processedText}\n\n${referencesText}`;
    navigator?.clipboard?.writeText(finalText);
    setIsCopied(true);
    toast.success(tr('chatPage.codeCopied'));
    setTimeout(() => {
      setIsCopied(false);
    }, 1500);
  };

  /**
   * Handles text-to-speech button press for a message
   * Tracks which message is currently being spoken
   *
   * @param message - Message to read aloud
   */
  const onReadTextPressHandler = (message: Message) => {
    if (onReadTextPress) {
      onReadTextPress(message);
      setSpeakingMessageIdx(messages.indexOf(message));
    }
  };

  /**
   * Renders action buttons for a message (copy, share, play, etc.)
   * Conditionally displays buttons based on available functionality
   *
   * @param message - Message to render options for
   * @returns JSX element containing message option buttons
   */
  const renderMessageOptions = (message: Message) => {
    const isSpeak = speakingMessageIdx === messages.indexOf(message);

    return (
      <AssistantOptionContainer isUser={message.role === 'user'} isRTL={isRTL}>
        {onReadTextPress && !shared && (
          <OptionIconContainer
            onClick={() => onReadTextPressHandler(message)}
            whileTap={{ opacity: 0.8, scale: 0.96 }}
            disabled={!message?.content}
          >
            {isSpeaking && isSpeak ? (
              <IoStopOutline size={theme.text.s5} color={theme.text.optionIcon} />
            ) : (
              <IoPlayOutline size={theme.text.s5} color={theme.text.optionIcon} />
            )}
            {isSpeaking && isSpeak ? 'Stop' : 'Play'}
          </OptionIconContainer>
        )}
        <OptionIconContainer
          onClick={() => copyToClipboard(message?.content || '', message.references)}
          whileTap={{ opacity: 0.8, scale: 0.96 }}
          disabled={isCopied}
        >
          {isCopied ? (
            <IoCheckmark size={theme.text.s5} color={theme.text.optionIcon} />
          ) : (
            <IoCopyOutline size={theme.text.s5} color={theme.text.optionIcon} />
          )}
          Copy
        </OptionIconContainer>
        {/* Render Share button only if not in shared mode */}
        {!shared && onSharePress && (
          <OptionIconContainer onClick={() => onSharePress()} whileTap={{ opacity: 0.8, scale: 0.96 }}>
            <RiShare2Line size={theme.text.s5} color={theme.text.optionIcon} />
            Share
          </OptionIconContainer>
        )}
        {!isRTL && <OptionIconContainer
          onClick={() => exportChatToPdf(messages)}
          whileTap={{
            opacity: 0.8,
            scale: 0.96,
          }}
        >
          <TbFileExport size={theme.text.s5} color={theme.text.optionIcon} />
          Export
        </OptionIconContainer>}
        {!shared && onAddToFavorite && (
          <OptionIconContainer onClick={() => onAddToFavorite()} whileTap={{ opacity: 0.8, scale: 0.96 }}>
            {isFavorite ? (
              <GoStarFill size={theme.text.s5} color={theme.text.optionIcon} />
            ) : (
              <GoStar strokeWidth={0.5} size={theme.text.s5} color={theme.text.optionIcon} />
            )}
            {isFavorite ? 'Remove from' : 'Add to'} Favorite
          </OptionIconContainer>
        )}
      </AssistantOptionContainer>
    );
  };

  /**
   * Handles clicks on links within messages
   * Opens links in a new window/tab
   *
   * @param event - Mouse click event
   */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLAnchorElement;
    if (target.tagName === 'A' && target.href) {
      event.preventDefault();
      openUrl(target.href);
    }
  };

  const handleQuickQuestionPress = (question: string) => {
    if (onQuickQuestionPress) onQuickQuestionPress(question);
  };

  // Add the new implementation for tryWithOtherCategory
  const tryWithOtherCategory = (question: string) => {
    if (onTryWithOtherCategory) {
      onTryWithOtherCategory(question);
    }
  };

  /**
   * Process message content for display
   * Handles code blocks and streaming typewriter effects
   *
   * @param message - Message object to render
   * @returns JSX elements for displaying the message content
   */
  const renderMessage = (message: Message) => {
    // Function to process references and replace with styled tooltips
    const processReferences = (content: string) => {
      return content.replace(/\[([^\]]+)\](?:\((https?:\/\/[^\)]+)\))?/g, (match, text, url, offset, fullString) => {
        const prevChar = offset > 0 ? fullString[offset - 1] : '';
        const prefix = prevChar && prevChar !== ' ' ? ' ' : '';
        // If a URL is provided in parentheses, use it.
        // Otherwise, check if the text itself is a valid URL.
        const finalUrl = url || (/^https?:\/\/\S+$/.test(text) ? text : null);
        // If no valid URL is found, return the original match.
        if (!finalUrl) {
          return match;
        }
        return `${prefix}[↗](${finalUrl})`;
      });
    };

    const parts = message.content.split(/(```[^`]*```)/g);

    return parts.map((part, index) => {
      const isCodeBlock = part.startsWith('```') && part.endsWith('```');

      if (isCodeBlock) {
        const cleanCode = part.slice(3, -3);
        const firstLineBreak = cleanCode.indexOf('\n');
        const code = firstLineBreak > -1 ? cleanCode.slice(firstLineBreak + 1) : cleanCode;

        return (
          <pre key={`${message.id}-${index}`}>
            <CodeDisplayBlock code={code} />
          </pre>
        );
      } else {
        if ((isLoading || isLoadingMessage) && messages.indexOf(message) === messages.length - 1) {
          return (
            <CustomTypeWriter
              key={`${message.id}-${index}`}
              speed={isLoading || isLoadingMessage ? 30 : 5}
              text={processReferences(part) || ''}
              isRTL={isRTL}
            />
          );
        }
        return (
          <span className="message" key={`${message.id}-${index}`}>
            {renderArticleContent(processReferences(part), {
              color: theme.chatList.text,
              references: message.references,
              isRTL: isRTL
            })}
          </span>
        );
      }
    });
  };

  // Function to scroll to bottom
  const scrollToBottom = () => {
    if (containerRef.current) {
      // Using setTimeout to ensure DOM updates are complete
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTo({
            top: containerRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }
      }, 100);
    }
  };

  // Function to force scroll to bottom immediately (no animation)
  const forceScrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'auto',
      });
    }
  };

  // Set up scroll listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Handle window resize
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      // Clear any existing timeout
      clearTimeout(resizeTimeout);

      // Set a new timeout to handle the resize after it's complete
      resizeTimeout = setTimeout(() => {
        if (shouldAutoScroll) {
          forceScrollToBottom();
        }
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [shouldAutoScroll]);

  // Scroll effect for messages and loading states
  useEffect(() => {
    // Only auto-scroll if we haven't disabled auto-scrolling (by scrolling up)
    if (shouldAutoScroll) {
      scrollToBottom();
    }
  }, [messages, isLoading, isLoadingMessage, shouldAutoScroll]);

  // Initial auto-scroll when component mounts
  useEffect(() => {
    // Set initial auto-scroll to true
    setShouldAutoScroll(true);

    // Initial scroll to bottom
    forceScrollToBottom();

    // Add a second delayed scroll to handle any late-rendering content
    const timeout = setTimeout(() => {
      if (shouldAutoScroll) {
        scrollToBottom();
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  // Handle first time messages load
  useEffect(() => {
    if (messages.length === 0) {
      hasScrolled.current = false;
      setShouldAutoScroll(true);
    } else if (messages.length > 0 && !hasScrolled.current) {
      // Force scroll on first load regardless of shouldAutoScroll state
      forceScrollToBottom();

      // Then do a smooth scroll after a short delay
      setTimeout(() => {
        forceScrollToBottom();
      }, 200);

      hasScrolled.current = true;
    }
  }, [messages]);

  // Watch for new messages being added to trigger auto-scroll
  useEffect(() => {
    if (shouldAutoScroll && messages.length > 0) {
      scrollToBottom();
    }
  }, [messages.length, shouldAutoScroll]);

  // Reset auto-scroll when starting a new conversation
  useEffect(() => {
    if (messages.length === 0) {
      setShouldAutoScroll(true);
    }
  }, [messages.length === 0]);

  // Effect for handling fullHeight changes
  useEffect(() => {
    if (fullHeight && messages.length > 0) {
      forceScrollToBottom();
      setTimeout(() => {
        if (shouldAutoScroll) {
          scrollToBottom();
        }
      }, 100);
    }
  }, [fullHeight, messages.length]);

  // Effect for loading message animation
  useEffect(() => {
    if (isLoadingMessage) {
      const words = ['Analyzing...', 'Generating response...', 'Processing...'];
      let index = 0;
      const interval = setInterval(() => {
        setDynamicWords(words[index % words.length]);
        index++;
      }, 1500);
      return () => clearInterval(interval);
    }
  }, [isLoadingMessage]);

  return (
    <Container ref={containerRef} isRTL={isRTL} fullHeight={fullHeight}>
      {messages.length === 0 &&
        (isLoadingMessages ? (
          <MessagesContainer isRTL={isRTL} isEmpty={false} fullHeight={fullHeight}>
            <MessageSkeletons />
          </MessagesContainer>
        ) : (
          <EmptyMessageContainer isRTL={isRTL}>
            {/* Only show intro text when not loading */}
            {!shared && categories !== undefined && !isLoading && (
              <AnimatedIntroText variants={textVariants} initial="hidden" animate="visible">
                {categories && categories.length > 0
                  ? 'Select a category filter and ask your medical question'
                  : 'Ask your medical question'}
              </AnimatedIntroText>
            )}

            {/* Show "No messages" text in shared mode only */}
            {shared && (
              <AnimatedIntroText variants={textVariants} initial="hidden" animate="visible">
                No messages have been shared yet.
              </AnimatedIntroText>
            )}

            {/* Only show questions if not in shared mode and not loading */}
            {!shared && categories !== undefined && !isLoadingMessages && !isLoading && (
              <QuickQuestionsContainer isRTL={isRTL}>
                {categories && categories.length > 0 ? (
                  /* Show category bullets when categories are available */
                  <>
                    {categories.map((category) => (
                      <CategoryBullet
                        key={category.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        onClick={() => {
                          if (onCategoryPress) onCategoryPress(category);
                        }}
                        isSelected={String(selectedCategory) === String(category.id)}
                      >
                        {category.name}
                      </CategoryBullet>
                    ))}
                  </>
                ) : (
                  /* Show default fast questions as fallback when no categories are available */
                  quickQuestions.map((question, idx) => (
                    <QuickQuestion
                      key={idx}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleQuickQuestionPress(question)}
                    >
                      {question}
                    </QuickQuestion>
                  ))
                )}
              </QuickQuestionsContainer>
            )}
          </EmptyMessageContainer>
        ))}

      {/* Only render MessagesContainer if there are messages or we're loading a message */}
      {messages.length > 0 || isLoadingMessage ? (
        <MessagesContainer
          id="scroller"
          onScroll={handleScroll}
          isRTL={isRTL}
          isEmpty={messages.length === 0 && !isLoadingMessage}
          fullHeight={fullHeight}
        >
          {isLoadingMessage && (
            <LoadingBubbleContainer layout={!(isLoadingMessage || isLoading)}>
              <AvatarContainer>
                <Image
                  src={require('../../assets/images/favicon.png')}
                  alt="GenioChat Logo"
                  style={{ width: '70%', height: '70%' }}
                />
              </AvatarContainer>
              <LoadingAnimation>{dynamicWords}</LoadingAnimation>
            </LoadingBubbleContainer>
          )}
          {messages.map((message, idx) => (
            <MessageItem
              key={message.id}
              isUser={message.role === 'user'}
              isRTL={isRTL}
              layout={!(isLoadingMessage || isLoading)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{
                opacity: { duration: 0.1 },
                layout: {
                  type: 'spring',
                  bounce: 0.3,
                  duration: messages.indexOf(message) * 0.05 + 0.2,
                },
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'center',
                  paddingBottom: '1%',
                  flexDirection: isRTL ? 'row-reverse' : 'row'
                }}
              >
                {message.role === 'user' && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: '1rem',
                      justifyContent: 'flex-end',
                      flexDirection: isRTL ? 'row-reverse' : 'row'
                    }}
                  >
                    <MessageBubble isUser={message.role === 'user'} isRTL={isRTL}>
                      {message.content || ''}
                      {!shared && categories && categories.length > 1 && (
                        <CategoryLink
                          whileTap={{ scale: 0.98 }}
                          onClick={() => tryWithOtherCategory(message.content)}
                          isRTL={isRTL}
                        >
                          <HiArrowPath size={14} />
                          <span>Try with different category</span>
                        </CategoryLink>
                      )}
                    </MessageBubble>
                    <AvatarContainer>
                      {imageUri && !isImageError ? (
                        <Image alt="user" src={imageUri} onError={() => setIsImageError(true)} />
                      ) : (
                        <BiUser size="55%" color={theme.text.light + 'BB'} />
                      )}
                    </AvatarContainer>
                  </div>
                )}
                {message.role === 'assistant' && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: '1rem',
                      position: 'relative',
                      flexDirection: isRTL ? 'row-reverse' : 'row'
                    }}
                  >
                    <AvatarContainer>
                      <Image
                        src={require('../../assets/images/favicon.png')}
                        alt="GenioChat Logo"
                        style={{ width: '70%', height: '70%' }}
                      />
                    </AvatarContainer>
                    <AssistantMessageContainer key={message.id} onClick={handleClick} isRTL={isRTL}>
                      {message.role === 'assistant' && (
                        <>
                          {isLoadingMessage && !message.references?.length && idx === messages.length - 1 ? (
                            <LoadingAnimation>{dynamicWords}</LoadingAnimation>
                          ) : (
                            <>
                              {message.references?.length > 0 && (
                                <ReferencesCarousel
                                  references={convertReferencesToLinks(message.references)}
                                  simulateLoading={!message.content && message.references?.length > 0}
                                  isRTL={false}
                                />
                              )}
                              {!message.content && message.references?.length > 0 && (
                                <LoadingAnimation>{dynamicWords}</LoadingAnimation>
                              )}
                              {renderMessage(message)}
                            </>
                          )}
                        </>
                      )}
                      {(idx !== 0 || !isLoading) && renderMessageOptions(message)}
                    </AssistantMessageContainer>
                  </div>
                )}
              </div>
            </MessageItem>
          ))}
        </MessagesContainer>
      ) : (
        <div ref={messagesEndRef} />
      )}
      {messages.length > 0 && <div ref={messagesEndRef} />}
    </Container>
  );
};

export default ChatList;
